// Full EN/AR content. t('path.to.key') resolves nested nodes (strings, arrays, objects).
const translations = {
  en: {
    dir: "ltr",
    common: {
      ask_ai: "Ask BuyDubai AI", become_partner: "Become a Partner", sign_in: "Sign In",
      dashboard: "Dashboard", logout: "Log out", learn_more: "Learn more", save: "Save",
      compare: "Compare", request_quote: "Request Quote", request_viewing: "Request Viewing",
      book: "Book", contact: "Contact Provider", create_project: "Create Project",
      why: "Why recommended", sources: "Sources", demo: "Demo data", live: "Live",
      coming: "Coming soon", beta: "Private beta", loading: "Loading…",
      error: "Something went wrong. Please try again.", retry: "Retry", select: "Select",
      submit: "Submit", submitting: "Submitting…", start_project: "Start this project",
      view: "View details", back: "Back", generate_cma: "Generate CMA", export: "Export",
      verified: "Verified", start_goal: "Start with your goal, not a category.",
    },
    nav: {
      ask: "Ask BuyDubai AI", explore: "Explore", projects: "Projects", property: "Property",
      services: "Services", experiences: "Experiences", business: "For Business",
      partners: "Partners", about: "About", trust: "Trust", broker: "Broker CMA",
    },
    footer: {
      blurb: "The AI platform for Dubai. Instead of searching many websites, tell BuyDubai.ai what you need — it discovers, compares and helps you take action.",
      powered: "Powered by Buy-Tech.ai",
      explore_h: "Platform", company_h: "Company",
      note: "Sponsored content is always labeled. We never fabricate businesses, reviews, prices or listings.",
    },
    home: {
      badge: "The AI platform for Dubai",
      h1a: "Ask Dubai.", h1b: "Get it done.",
      sub: "Tell BuyDubai.ai what you need. Our AI platform helps you discover, compare and take action across Dubai through one conversation.",
      placeholder: "What do you want to do in Dubai?",
      prompts: [
        "Find me a 2BR in Dubai Marina under AED 2M.",
        "Is this asking price fair for Dubai Marina?",
        "Compare 2-bedroom prices in Marina vs JVC.",
      ],
      five: [
        { h: "One conversation", p: "Instead of searching many Dubai websites, just tell BuyDubai.ai what you need." },
        { h: "Compare & decide", p: "It finds trusted providers, organizes projects and surfaces the next step." },
        { h: "Explainable & trusted", p: "Verified providers, clear sources and reasons behind every recommendation." },
      ],
      cap_h: "One AI. The whole city.",
      cap_sub: "Property is our first live vertical — everything else is coming, honestly labeled.",
      property_desc: "Discovery, pricing intelligence, comparable sales, buyer qualification and viewing requests — all in one conversation.",
      coming: [
        { h: "Local Services", p: "Compare quotes from verified providers tonight." },
        { h: "Business Setup", p: "Free zones, licensing and visas made simple." },
        { h: "Relocation", p: "Family moves — homes, schools, movers, cars." },
      ],
      explore_h: "Explore Dubai by intent",
      cats: [
        { key: "property", name: "Property" }, { key: "services", name: "Services" },
        { key: "business", name: "Business" }, { key: "experiences", name: "Experiences" },
        { key: "cars", name: "Cars" }, { key: "relocation", name: "Relocation" },
        { key: "dining", name: "Dining" }, { key: "professional", name: "Professional Services" },
      ],
      tagline: "One conversation for what you need in Dubai.",
    },
    ask: {
      title: "Ask Dubai. Get it done.", eyebrow: "BuyDubai AI",
      intro: "Tell me what you need in Dubai. I can find and compare property, pull comparable sales, scope service quotes, and organize it into a project.",
      placeholder: "What do you want to do in Dubai?",
      thinking: "BuyDubai is thinking…",
      panel_project: "Project status", panel_follow: "Suggested follow-ups", panel_sources: "Why recommended · Sources",
      empty_project: "Start a conversation and BuyDubai will organize it into a goal-first project here.",
      empty_sources: "Every recommendation is backed by BuyDubai tools — never invented. Sources appear here.",
      fail: "Sorry — I couldn't reach the assistant just now. Please try again.",
      partial: "The answer was cut off.",
      retry: "Retry",
      listening: "Listening… speak now",
      voice_unsupported: "Voice input isn't supported in this browser.",
      track_project: "Track as a Project",
    },
    explore: {
      eyebrow: "Explore", title: "Start with your goal, not a category.",
      sub: "Elegant, verified profiles across Dubai — or just ask BuyDubai AI. Demo-labeled data only; we never invent businesses or reviews.",
      ask_cta: "Or ask BuyDubai AI instead",
      categories: [
        { key: "property", name: "Property", desc: "Discovery, pricing intelligence & viewings.", status: "live" },
        { key: "home-services", name: "Home Services", desc: "Compare quotes from verified providers.", status: "coming" },
        { key: "restaurants", name: "Restaurants", desc: "Reservations & curated recommendations.", status: "coming" },
        { key: "experiences", name: "Experiences", desc: "Tours, desert, yachts & family days out.", status: "coming" },
        { key: "business-services", name: "Business Services", desc: "Setup, licensing, banking & office.", status: "coming" },
        { key: "cars", name: "Cars", desc: "Buy, lease, rent & service.", status: "coming" },
        { key: "relocation", name: "Relocation", desc: "Visas, schools, movers & setup.", status: "coming" },
        { key: "education", name: "Education", desc: "Schools & universities matching.", status: "coming" },
        { key: "health", name: "Health & Wellness", desc: "Clinics, fitness & recovery.", status: "coming" },
        { key: "professional", name: "Professional Services", desc: "Legal, accounting & advisory.", status: "coming" },
      ],
      profile_h: "Sample verified profile",
      profile_name: "Prime Care Services", profile_cat: "Home Services · Dubai Marina",
      profile_hours: "Open 8am–10pm",
      profile_why: "Consistently fast response times and a strong verified score for AC & maintenance jobs in the Marina area. (Demo summary — real AI summaries connect at launch.)",
    },
    projects: {
      eyebrow: "Projects", title: "Start with your goal, not a category.",
      sub: "Every important goal becomes a project — with status, requirements, providers, quotes, appointments and next actions.",
      example_h: "Example project", example_name: "Moving to Dubai", example_status: "Planning",
      example_rows: [
        { k: "Property", v: "3 matches" }, { k: "Schools", v: "4 recommendations" },
        { k: "Movers", v: "Quotes pending" }, { k: "Car", v: "Not started" },
        { k: "Insurance", v: "Coming next" },
      ],
      goals: [
        { goal: "Buy a Property", status: "live", steps: ["Set budget & area", "Shortlist listings", "Book viewings", "Make an offer"] },
        { goal: "Move to Dubai", status: "coming", steps: ["Visa pathway", "Find a home", "Schools", "Movers"] },
        { goal: "Start a Business", status: "coming", steps: ["Choose free zone", "License", "Bank account", "Office"] },
        { goal: "Renovate a Home", status: "coming", steps: ["Define scope", "Get quotes", "Compare", "Schedule"] },
        { goal: "Get Service Quotes", status: "coming", steps: ["Describe the job", "Receive quotes", "Compare", "Book"] },
        { goal: "Buy a Car", status: "coming", steps: ["Set budget", "Compare models", "Finance", "Register"] },
        { goal: "Plan an Event", status: "coming", steps: ["Define brief", "Venues", "Vendors", "Book"] },
        { goal: "Plan an Experience", status: "coming", steps: ["Pick a date", "Choose activities", "Compare", "Reserve"] },
      ],
    },
    property: {
      badge: "Live first",
      title: "Property search should start with a conversation.",
      sub: "Discovery, comparisons, pricing intelligence, comparable sales, buyer qualification and viewing requests — ask BuyDubai and it handles the rest. Listings below are demo-labeled.",
      prompt_example: "Find a family-friendly 2BR under AED 2.5M with good schools nearby and strong resale potential.",
      ask_btn: "Ask about this",
      detail_facts: "Facts", detail_price: "Price analysis", detail_comps: "Comparable transactions",
      detail_area: "Area information", detail_broker: "Broker", detail_why: "Why recommended",
      market_fair: "Fairly priced", market_below: "Below market", market_above: "Above market",
      rera: "RERA permit", freshness: "Source freshness",
    },
    services: {
      eyebrow: "Private beta",
      title: "Get quotes. Compare. Book.",
      sub: "Describe the job once and BuyDubai gathers quotes from verified providers, then compares price, availability, response time, warranty and verified score. Below is a demo of the comparison you'll get.",
      cols: ["Provider", "Price", "Availability", "Response", "Select"],
      note: "Demo data — real-time quotes activate as partner integrations go live.",
      flow_h: "How a service request works",
      flow: [
        "You tell BuyDubai the problem in plain language.",
        "It creates a Service Project and matches verified providers.",
        "It requests quotes and compares them side by side.",
        "You pick a provider and book — with everything tracked in your project.",
      ],
    },
    experiences: {
      eyebrow: "Coming soon",
      title: "Plan unforgettable days in Dubai.",
      sub: "From desert escapes and yacht evenings to family days out and anniversary dinners — tell BuyDubai the occasion and it curates and books the experience.",
      cta: "Plan an experience",
      cards: [
        { h: "Anniversary evening", p: "Fine dining and a private moment, matched to your budget." },
        { h: "Desert & adventure", p: "Curated tours with verified operators." },
        { h: "Family day out", p: "Age-appropriate plans the whole family will love." },
        { h: "On the water", p: "Yachts and cruises for celebrations." },
      ],
    },
    business: {
      eyebrow: "For Business",
      title: "Start a business in Dubai.",
      sub: "Tell BuyDubai your business idea and it scopes the setup — structure, licensing, visas, banking and office — into one clear project.",
      cta: "Scope my setup",
      steps: [
        { h: "Choose your structure", p: "Mainland vs free zone, matched to your activity and budget." },
        { h: "Licensing & visas", p: "Trade license, establishment card, investor & staff visas." },
        { h: "Banking & office", p: "Corporate account guidance and flexible office solutions." },
      ],
    },
    partners: {
      eyebrow: "Partners", title: "Turn customer intent into business.",
      sub: "Join BuyDubai as a verified provider and reach customers at the moment of real intent.",
      benefits: [
        { h: "Qualified leads", p: "Reach customers when they're ready to act." },
        { h: "Quote & booking requests", p: "Receive structured requests, not cold clicks." },
        { h: "Verified profile", p: "A premium, verified profile inside BuyDubai." },
        { h: "Analytics", p: "See demand, conversion and response performance." },
      ],
      ai_h: "Future AI Employees for your business",
      ai: ["AI Receptionist", "AI Sales Agent", "AI WhatsApp Assistant", "AI Booking Agent", "AI CRM Follow-Up", "AI Marketing Agent"],
      form_h: "Become a Founding Partner",
      fields: { name: "Full name", company: "Company", category: "Business category",
        email: "Email", phone: "Phone / WhatsApp", website: "Website",
        areas: "Areas served", interest: "How would you like to partner?" },
      cats: ["Property / Broker", "Home Services", "Business Setup", "Relocation", "Restaurant", "Experiences", "Cars", "Professional Services", "Other"],
      success_h: "Application received",
      success_p: "Routed to partners@buydubai.ai. Our partnerships team will be in touch shortly.",
      dash_h: "Partner dashboard", dash_preview: "Preview",
      dash_stats: [["New leads", "24"], ["Quote requests", "11"], ["Response rate", "94%"]],
      dash_note: "Illustrative preview. Live analytics activate for onboarded partners.",
      broker_cta: "Open the Broker CMA workbench",
    },
    broker: {
      eyebrow: "Broker Workbench", title: "Comparative Market Analysis",
      sub: "Generate a defensible CMA from verified comparable transactions. The engine is deterministic — figures come from data, never from a language model.",
      tabs: ["CMA", "Reports", "Leads", "Listings", "Analytics", "Profile"],
      form: { area: "Area", building: "Building (optional)", bedrooms: "Bedrooms",
        type: "Property type", size: "Size (sqft)", price: "Asking price (AED)" },
      generate: "Generate CMA",
      soon: "Coming soon for onboarded brokers.",
      res: { confidence: "Confidence", level: "Comparable level", count: "Comparables used",
        median: "Median AED/sqft", p25: "P25", p75: "P75", subject: "Subject AED/sqft",
        deviation: "Deviation vs median", range: "Suggested pricing range",
        comps: "Comparable transactions", why: "Why selected", export: "Export report" },
      insufficient_h: "Insufficient comparable data",
      sign_in_hint: "Sign in to save and export CMA reports.",
      examples_h: "Try an example subject property",
      examples_note: "Prefilled subjects — press to run a full analysis.",
    },
    trust: {
      eyebrow: "Trust", title: "Built to be credible.",
      sub: "BuyDubai is designed so you can trust every recommendation.",
      principles: [
        { h: "Explainable AI", p: "Every recommendation shows why it was made and its sources." },
        { h: "Visible sources", p: "Where applicable, the data behind an answer is shown." },
        { h: "Sponsored is labeled", p: "Sponsored businesses are always clearly marked." },
        { h: "Payment ≠ ranking", p: "Paying never makes a provider the AI's recommended choice." },
      ],
      never_h: "What we never fabricate",
      never: ["Businesses", "Reviews", "Prices", "Quotes", "Availability", "Property listings", "Market transactions", "Government affiliations"],
      close: "We make no claims of government partnerships, live inventory, guarantees or user counts. Capabilities are labeled Live, Beta or Coming soon.",
    },
    about: {
      eyebrow: "About", title: "The AI platform for Dubai.",
      body: "BuyDubai.ai is not a portal, a directory, or a chatbot. It's one intelligent conversation that helps you discover, compare, decide, get quotes, book and complete important goals across Dubai. Specialist capabilities work silently behind a single assistant — you only ever talk to BuyDubai. Property is the first wedge, not the company.",
    },
    auth: {
      welcome: "Welcome back", create: "Create your account",
      welcome_sub: "Sign in to your dashboard.", create_sub: "Start building projects with BuyDubai.",
      name: "Full name", email: "Email", password: "Password",
      sign_in: "Sign in", sign_up: "Create account",
      to_register: "New to BuyDubai? Create an account", to_login: "Already have an account? Sign in",
      side_title: "Ask Dubai. Get it done.",
      side_sub: "Your projects, saved items, quotes and appointments — remembered across one conversation.",
    },
    dashboard: {
      eyebrow: "Dashboard", hello: "Hello",
      memory_link: "What BuyDubai knows about me",
      stats: ["Projects", "Saved items", "Quotes", "Appointments"],
      projects_h: "Your projects",
      empty: "Start with your goal, not a category.",
    },
    memory: {
      eyebrow: "Privacy", title: "What BuyDubai knows about me",
      sub: "You control your data. View, edit or delete it anytime. No sensitive data is stored without consent.",
      fields: { budget: "Budget (AED)", preferred_areas: "Preferred areas", family_size: "Family size",
        property_type: "Property type", moving_timeline: "Moving timeline", cuisines: "Favourite cuisines" },
      add: "Add preference", value: "Value", not_set: "Not set",
      save: "Save", delete_all: "Delete all",
    },
    legal: {
      privacy: "Privacy", terms: "Terms",
      privacy_body: [
        "BuyDubai.ai stores only the preferences and project data you choose to share. You can view, edit and delete everything from the \u201cWhat BuyDubai knows about me\u201d page.",
        "No sensitive data is collected without your consent. We never sell your personal data. AI conversations are used only to help complete your requests.",
        "Sponsored content is always labeled and paid partners never auto-become top recommendations.",
      ],
      terms_body: [
        "BuyDubai.ai is an AI platform that helps you discover, compare and act on goals across Dubai. Provider data shown during this launch phase is demo-labeled until real integrations are enabled.",
        "We make no claims of government partnerships, live inventory, guarantees, or user counts. Capabilities are labeled Live, Beta, or Coming soon.",
        "Powered by Buy-Tech.ai. Contact: partners@buydubai.ai",
      ],
    },
  },

  ar: {
    dir: "rtl",
    common: {
      ask_ai: "اسأل BuyDubai AI", become_partner: "كن شريكاً", sign_in: "تسجيل الدخول",
      dashboard: "لوحة التحكم", logout: "تسجيل الخروج", learn_more: "اعرف المزيد", save: "حفظ",
      compare: "قارن", request_quote: "اطلب عرض سعر", request_viewing: "اطلب معاينة",
      book: "احجز", contact: "تواصل مع المزود", create_project: "أنشئ مشروعاً",
      why: "لماذا نوصي بهذا", sources: "المصادر", demo: "بيانات تجريبية", live: "مباشر",
      coming: "قريباً", beta: "نسخة تجريبية خاصة", loading: "جارٍ التحميل…",
      error: "حدث خطأ ما. حاول مرة أخرى.", retry: "إعادة المحاولة", select: "اختر",
      submit: "إرسال", submitting: "جارٍ الإرسال…", start_project: "ابدأ هذا المشروع",
      view: "عرض التفاصيل", back: "رجوع", generate_cma: "إنشاء تحليل السوق", export: "تصدير",
      verified: "موثّق", start_goal: "ابدأ بهدفك، لا بالفئة.",
    },
    nav: {
      ask: "اسأل BuyDubai AI", explore: "استكشف", projects: "المشاريع", property: "العقارات",
      services: "الخدمات", experiences: "التجارب", business: "للأعمال",
      partners: "الشركاء", about: "من نحن", trust: "الثقة", broker: "تحليل السوق للوسطاء",
    },
    footer: {
      blurb: "منصة الذكاء الاصطناعي لدبي. بدلاً من البحث في مواقع كثيرة، أخبر BuyDubai.ai بما تحتاجه — فهو يكتشف ويقارن ويساعدك على اتخاذ الإجراء.",
      powered: "مُشغّل بواسطة Buy-Tech.ai",
      explore_h: "المنصة", company_h: "الشركة",
      note: "المحتوى المدفوع يُوضّح دائماً. لا نختلق أبداً أعمالاً أو تقييمات أو أسعاراً أو قوائم عقارية.",
    },
    home: {
      badge: "منصة الذكاء الاصطناعي لدبي",
      h1a: "اسأل دبي.", h1b: "وأنجز مهامك.",
      sub: "أخبر BuyDubai.ai بما تحتاجه. تساعدك منصتنا على الاكتشاف والمقارنة واتخاذ الإجراء في جميع أنحاء دبي من خلال محادثة واحدة.",
      placeholder: "ماذا تريد أن تفعل في دبي؟",
      prompts: [
        "ابحث لي عن شقة بغرفتين في دبي مارينا بأقل من مليوني درهم.",
        "هل هذا السعر المطلوب عادل في دبي مارينا؟",
        "قارن أسعار الشقق بغرفتين في مارينا مقابل قرية جميرا الدائرية.",
      ],
      five: [
        { h: "محادثة واحدة", p: "بدلاً من البحث في مواقع دبي الكثيرة، أخبر BuyDubai.ai بما تحتاجه فقط." },
        { h: "قارن وقرّر", p: "يجد مزوّدين موثوقين، وينظّم المشاريع، ويُبرز الخطوة التالية." },
        { h: "قابل للتفسير وموثوق", p: "مزوّدون موثّقون، ومصادر واضحة، وأسباب وراء كل توصية." },
      ],
      cap_h: "ذكاء اصطناعي واحد. المدينة بأكملها.",
      cap_sub: "العقارات هي أول مجال مباشر لدينا — وكل ما عداه قادم، بعلامات صريحة.",
      property_desc: "الاكتشاف، وذكاء التسعير، والمبيعات المماثلة، وتأهيل المشتري، وطلبات المعاينة — كل ذلك في محادثة واحدة.",
      coming: [
        { h: "الخدمات المحلية", p: "قارن العروض من مزوّدين موثّقين الليلة." },
        { h: "تأسيس الأعمال", p: "مناطق حرة وتراخيص وتأشيرات بكل بساطة." },
        { h: "الانتقال", p: "انتقال العائلات — منازل، مدارس، شركات نقل، سيارات." },
      ],
      explore_h: "استكشف دبي حسب هدفك",
      cats: [
        { key: "property", name: "العقارات" }, { key: "services", name: "الخدمات" },
        { key: "business", name: "الأعمال" }, { key: "experiences", name: "التجارب" },
        { key: "cars", name: "السيارات" }, { key: "relocation", name: "الانتقال" },
        { key: "dining", name: "المطاعم" }, { key: "professional", name: "الخدمات المهنية" },
      ],
      tagline: "محادثة واحدة لكل ما تحتاجه في دبي.",
    },
    ask: {
      title: "اسأل دبي. وأنجز مهامك.", eyebrow: "BuyDubai AI",
      intro: "أخبرني بما تحتاجه في دبي. يمكنني البحث ومقارنة العقارات، واستخراج المبيعات المماثلة، وتحديد عروض الخدمات، وتنظيم ذلك في مشروع.",
      placeholder: "ماذا تريد أن تفعل في دبي؟",
      thinking: "BuyDubai يفكّر…",
      panel_project: "حالة المشروع", panel_follow: "متابعات مقترحة", panel_sources: "لماذا نوصي · المصادر",
      empty_project: "ابدأ محادثة وسينظّمها BuyDubai هنا في مشروع يبدأ بالهدف.",
      empty_sources: "كل توصية مدعومة بأدوات BuyDubai — لا تُختلق أبداً. تظهر المصادر هنا.",
      fail: "عذراً — لم أتمكن من الوصول إلى المساعد الآن. حاول مرة أخرى.",
      partial: "تم قطع الإجابة.",
      retry: "إعادة المحاولة",
      listening: "أستمع… تحدّث الآن",
      voice_unsupported: "الإدخال الصوتي غير مدعوم في هذا المتصفح.",
      track_project: "تتبّع كمشروع",
    },
    explore: {
      eyebrow: "استكشف", title: "ابدأ بهدفك، لا بالفئة.",
      sub: "ملفات أنيقة وموثّقة في جميع أنحاء دبي — أو اسأل BuyDubai AI مباشرة. بيانات تجريبية فقط؛ لا نختلق أعمالاً أو تقييمات.",
      ask_cta: "أو اسأل BuyDubai AI بدلاً من ذلك",
      categories: [
        { key: "property", name: "العقارات", desc: "الاكتشاف وذكاء التسعير والمعاينات.", status: "live" },
        { key: "home-services", name: "الخدمات المنزلية", desc: "قارن العروض من مزوّدين موثّقين.", status: "coming" },
        { key: "restaurants", name: "المطاعم", desc: "الحجوزات وتوصيات مختارة.", status: "coming" },
        { key: "experiences", name: "التجارب", desc: "جولات وصحراء ويخوت وأيام عائلية.", status: "coming" },
        { key: "business-services", name: "خدمات الأعمال", desc: "التأسيس والترخيص والبنوك والمكاتب.", status: "coming" },
        { key: "cars", name: "السيارات", desc: "شراء وتأجير وإيجار وصيانة.", status: "coming" },
        { key: "relocation", name: "الانتقال", desc: "تأشيرات ومدارس وشركات نقل وتجهيز.", status: "coming" },
        { key: "education", name: "التعليم", desc: "مطابقة المدارس والجامعات.", status: "coming" },
        { key: "health", name: "الصحة والعافية", desc: "عيادات ولياقة وتعافٍ.", status: "coming" },
        { key: "professional", name: "الخدمات المهنية", desc: "قانونية ومحاسبية واستشارية.", status: "coming" },
      ],
      profile_h: "نموذج ملف موثّق",
      profile_name: "Prime Care Services", profile_cat: "خدمات منزلية · دبي مارينا",
      profile_hours: "يعمل 8ص–10م",
      profile_why: "أوقات استجابة سريعة باستمرار ودرجة توثيق عالية لأعمال التكييف والصيانة في منطقة المارينا. (ملخص تجريبي — الملخصات الحقيقية بالذكاء الاصطناعي تُفعّل عند الإطلاق.)",
    },
    projects: {
      eyebrow: "المشاريع", title: "ابدأ بهدفك، لا بالفئة.",
      sub: "كل هدف مهم يصبح مشروعاً — بحالة ومتطلبات ومزوّدين وعروض ومواعيد وخطوات تالية.",
      example_h: "مثال على مشروع", example_name: "الانتقال إلى دبي", example_status: "قيد التخطيط",
      example_rows: [
        { k: "العقار", v: "3 نتائج" }, { k: "المدارس", v: "4 توصيات" },
        { k: "شركات النقل", v: "عروض قيد الانتظار" }, { k: "السيارة", v: "لم تبدأ" },
        { k: "التأمين", v: "قادم لاحقاً" },
      ],
      goals: [
        { goal: "شراء عقار", status: "live", steps: ["حدّد الميزانية والمنطقة", "اختر القوائم", "احجز المعاينات", "قدّم عرضاً"] },
        { goal: "الانتقال إلى دبي", status: "coming", steps: ["مسار التأشيرة", "إيجاد منزل", "المدارس", "شركات النقل"] },
        { goal: "تأسيس شركة", status: "coming", steps: ["اختر منطقة حرة", "الترخيص", "حساب بنكي", "مكتب"] },
        { goal: "تجديد منزل", status: "coming", steps: ["حدّد النطاق", "اطلب عروضاً", "قارن", "جدولة"] },
        { goal: "الحصول على عروض خدمات", status: "coming", steps: ["صف المهمة", "استلم العروض", "قارن", "احجز"] },
        { goal: "شراء سيارة", status: "coming", steps: ["حدّد الميزانية", "قارن الطرازات", "التمويل", "التسجيل"] },
        { goal: "تخطيط فعالية", status: "coming", steps: ["حدّد المتطلبات", "الأماكن", "المزوّدون", "احجز"] },
        { goal: "تخطيط تجربة", status: "coming", steps: ["اختر تاريخاً", "اختر الأنشطة", "قارن", "احجز"] },
      ],
    },
    property: {
      badge: "مباشر أولاً",
      title: "يجب أن يبدأ البحث عن العقار بمحادثة.",
      sub: "الاكتشاف والمقارنات وذكاء التسعير والمبيعات المماثلة وتأهيل المشتري وطلبات المعاينة — اسأل BuyDubai وسيتولّى الباقي. القوائم أدناه تجريبية.",
      prompt_example: "ابحث عن شقة بغرفتين مناسبة للعائلة بأقل من 2.5 مليون درهم قرب مدارس جيدة وبقيمة إعادة بيع قوية.",
      ask_btn: "اسأل عن هذا",
      detail_facts: "الحقائق", detail_price: "تحليل السعر", detail_comps: "المعاملات المماثلة",
      detail_area: "معلومات المنطقة", detail_broker: "الوسيط", detail_why: "لماذا نوصي",
      market_fair: "سعر عادل", market_below: "أقل من السوق", market_above: "أعلى من السوق",
      rera: "تصريح ريرا", freshness: "حداثة المصدر",
    },
    services: {
      eyebrow: "نسخة تجريبية خاصة",
      title: "احصل على عروض. قارن. احجز.",
      sub: "صف المهمة مرة واحدة، ويجمع BuyDubai العروض من مزوّدين موثّقين، ثم يقارن السعر والتوفّر ووقت الاستجابة والضمان ودرجة التوثيق. أدناه عرض تجريبي لما ستحصل عليه.",
      cols: ["المزوّد", "السعر", "التوفّر", "الاستجابة", "اختيار"],
      note: "بيانات تجريبية — تُفعّل العروض الفورية عند ربط الشركاء.",
      flow_h: "كيف يعمل طلب الخدمة",
      flow: [
        "تخبر BuyDubai بالمشكلة بلغة بسيطة.",
        "ينشئ مشروع خدمة ويطابق مزوّدين موثّقين.",
        "يطلب العروض ويقارنها جنباً إلى جنب.",
        "تختار مزوّداً وتحجز — مع تتبّع كل شيء في مشروعك.",
      ],
    },
    experiences: {
      eyebrow: "قريباً",
      title: "خطّط لأيام لا تُنسى في دبي.",
      sub: "من رحلات الصحراء وأمسيات اليخوت إلى الأيام العائلية وعشاء ذكرى الزواج — أخبر BuyDubai بالمناسبة وسينسّق التجربة ويحجزها.",
      cta: "خطّط لتجربة",
      cards: [
        { h: "أمسية ذكرى زواج", p: "عشاء فاخر ولحظة خاصة تناسب ميزانيتك." },
        { h: "الصحراء والمغامرة", p: "جولات مختارة مع مشغّلين موثّقين." },
        { h: "يوم عائلي", p: "خطط مناسبة لكل الأعمار تحبها العائلة." },
        { h: "على الماء", p: "يخوت ورحلات بحرية للاحتفالات." },
      ],
    },
    business: {
      eyebrow: "للأعمال",
      title: "أسّس شركتك في دبي.",
      sub: "أخبر BuyDubai بفكرة مشروعك وسيحدّد خطوات التأسيس — الهيكل والترخيص والتأشيرات والبنوك والمكتب — في مشروع واحد واضح.",
      cta: "حدّد خطوات تأسيسي",
      steps: [
        { h: "اختر هيكلك", p: "برّ رئيسي أو منطقة حرة، بما يناسب نشاطك وميزانيتك." },
        { h: "الترخيص والتأشيرات", p: "رخصة تجارية وبطاقة منشأة وتأشيرات المستثمر والموظفين." },
        { h: "البنوك والمكتب", p: "إرشاد لحساب الشركة وحلول مكتبية مرنة." },
      ],
    },
    partners: {
      eyebrow: "الشركاء", title: "حوّل نية العميل إلى أعمال.",
      sub: "انضم إلى BuyDubai كمزوّد موثّق وتواصل مع العملاء في لحظة النية الحقيقية.",
      benefits: [
        { h: "عملاء مؤهّلون", p: "تواصل مع العملاء عندما يكونون جاهزين للتنفيذ." },
        { h: "طلبات عروض وحجوزات", p: "استلم طلبات منظّمة، لا نقرات باردة." },
        { h: "ملف موثّق", p: "ملف مميّز وموثّق داخل BuyDubai." },
        { h: "تحليلات", p: "شاهد الطلب والتحويل وأداء الاستجابة." },
      ],
      ai_h: "موظفو الذكاء الاصطناعي القادمون لأعمالك",
      ai: ["موظف استقبال بالذكاء الاصطناعي", "وكيل مبيعات بالذكاء الاصطناعي", "مساعد واتساب بالذكاء الاصطناعي", "وكيل حجوزات بالذكاء الاصطناعي", "متابعة CRM بالذكاء الاصطناعي", "وكيل تسويق بالذكاء الاصطناعي"],
      form_h: "كن شريكاً مؤسِّساً",
      fields: { name: "الاسم الكامل", company: "الشركة", category: "فئة العمل",
        email: "البريد الإلكتروني", phone: "الهاتف / واتساب", website: "الموقع الإلكتروني",
        areas: "المناطق المخدومة", interest: "كيف ترغب في الشراكة؟" },
      cats: ["عقارات / وسيط", "خدمات منزلية", "تأسيس أعمال", "انتقال", "مطعم", "تجارب", "سيارات", "خدمات مهنية", "أخرى"],
      success_h: "تم استلام الطلب",
      success_p: "تم توجيهه إلى partners@buydubai.ai. سيتواصل معك فريق الشراكات قريباً.",
      dash_h: "لوحة الشريك", dash_preview: "معاينة",
      dash_stats: [["عملاء جدد", "24"], ["طلبات عروض", "11"], ["معدل الاستجابة", "94%"]],
      dash_note: "معاينة توضيحية. تُفعّل التحليلات الحية للشركاء المسجّلين.",
      broker_cta: "افتح ورشة تحليل السوق للوسطاء",
    },
    broker: {
      eyebrow: "ورشة الوسيط", title: "تحليل السوق المقارن",
      sub: "أنشئ تحليل سوق مدعوماً من معاملات مماثلة موثّقة. المحرّك حتمي — الأرقام من البيانات، وليست من نموذج لغوي.",
      tabs: ["تحليل السوق", "التقارير", "العملاء", "القوائم", "التحليلات", "الملف"],
      form: { area: "المنطقة", building: "المبنى (اختياري)", bedrooms: "غرف النوم",
        type: "نوع العقار", size: "المساحة (قدم مربع)", price: "السعر المطلوب (درهم)" },
      generate: "إنشاء تحليل السوق",
      soon: "قريباً للوسطاء المسجّلين.",
      res: { confidence: "الثقة", level: "مستوى المقارنة", count: "المعاملات المستخدمة",
        median: "الوسيط درهم/قدم", p25: "المئين 25", p75: "المئين 75", subject: "العقار درهم/قدم",
        deviation: "الانحراف عن الوسيط", range: "نطاق التسعير المقترح",
        comps: "المعاملات المماثلة", why: "سبب الاختيار", export: "تصدير التقرير" },
      insufficient_h: "بيانات مقارنة غير كافية",
      sign_in_hint: "سجّل الدخول لحفظ وتصدير تقارير تحليل السوق.",
      examples_h: "جرّب عقاراً نموذجياً",
      examples_note: "عقارات معبّأة مسبقاً — اضغط لتشغيل تحليل كامل.",
    },
    trust: {
      eyebrow: "الثقة", title: "مصمّمة لتكون جديرة بالثقة.",
      sub: "صُمّم BuyDubai بحيث تثق بكل توصية.",
      principles: [
        { h: "ذكاء اصطناعي قابل للتفسير", p: "كل توصية تُظهر سبب اتخاذها ومصادرها." },
        { h: "مصادر مرئية", p: "حيثما أمكن، تُعرض البيانات وراء الإجابة." },
        { h: "المدفوع موضّح", p: "الأعمال المدعومة تُوضّح دائماً بوضوح." },
        { h: "الدفع ليس ترتيباً", p: "الدفع لا يجعل مزوّداً أبداً الخيار الموصى به من الذكاء الاصطناعي." },
      ],
      never_h: "ما لا نختلقه أبداً",
      never: ["الأعمال", "التقييمات", "الأسعار", "العروض", "التوفّر", "قوائم العقارات", "معاملات السوق", "الارتباطات الحكومية"],
      close: "لا ندّعي أي شراكات حكومية أو مخزوناً مباشراً أو ضمانات أو أعداد مستخدمين. القدرات مُعلّمة بمباشر أو تجريبي أو قريباً.",
    },
    about: {
      eyebrow: "من نحن", title: "منصة الذكاء الاصطناعي لدبي.",
      body: "BuyDubai.ai ليست بوابة ولا دليلاً ولا روبوت دردشة. إنها محادثة ذكية واحدة تساعدك على الاكتشاف والمقارنة واتخاذ القرار والحصول على العروض والحجز وإنجاز أهدافك المهمة في دبي. تعمل القدرات المتخصصة بصمت خلف مساعد واحد — أنت تتحدث فقط إلى BuyDubai. العقارات هي البداية، وليست الشركة كلها.",
    },
    auth: {
      welcome: "مرحباً بعودتك", create: "أنشئ حسابك",
      welcome_sub: "سجّل الدخول إلى لوحتك.", create_sub: "ابدأ ببناء المشاريع مع BuyDubai.",
      name: "الاسم الكامل", email: "البريد الإلكتروني", password: "كلمة المرور",
      sign_in: "تسجيل الدخول", sign_up: "إنشاء حساب",
      to_register: "جديد على BuyDubai؟ أنشئ حساباً", to_login: "لديك حساب؟ سجّل الدخول",
      side_title: "اسأل دبي. وأنجز مهامك.",
      side_sub: "مشاريعك وعناصرك المحفوظة وعروضك ومواعيدك — محفوظة عبر محادثة واحدة.",
    },
    dashboard: {
      eyebrow: "لوحة التحكم", hello: "مرحباً",
      memory_link: "ما يعرفه BuyDubai عني",
      stats: ["المشاريع", "العناصر المحفوظة", "العروض", "المواعيد"],
      projects_h: "مشاريعك",
      empty: "ابدأ بهدفك، لا بالفئة.",
    },
    memory: {
      eyebrow: "الخصوصية", title: "ما يعرفه BuyDubai عني",
      sub: "أنت تتحكم ببياناتك. اعرضها أو عدّلها أو احذفها في أي وقت. لا تُخزَّن بيانات حساسة دون موافقة.",
      fields: { budget: "الميزانية (درهم)", preferred_areas: "المناطق المفضّلة", family_size: "حجم العائلة",
        property_type: "نوع العقار", moving_timeline: "توقيت الانتقال", cuisines: "المطابخ المفضّلة" },
      add: "أضف تفضيلاً", value: "القيمة", not_set: "غير محدّد",
      save: "حفظ", delete_all: "حذف الكل",
    },
    legal: {
      privacy: "الخصوصية", terms: "الشروط",
      privacy_body: [
        "يخزّن BuyDubai.ai فقط التفضيلات وبيانات المشاريع التي تختار مشاركتها. يمكنك عرض وتعديل وحذف كل شيء من صفحة \u201cما يعرفه BuyDubai عني\u201d.",
        "لا تُجمع أي بيانات حساسة دون موافقتك. لا نبيع بياناتك الشخصية أبداً. تُستخدم المحادثات فقط لمساعدتك في إنجاز طلباتك.",
        "المحتوى المدفوع يُوضّح دائماً، والشركاء المدفوعون لا يصبحون تلقائياً توصيات أولى.",
      ],
      terms_body: [
        "BuyDubai.ai منصة ذكاء اصطناعي تساعدك على الاكتشاف والمقارنة واتخاذ إجراءات بشأن أهدافك في دبي. بيانات المزوّدين المعروضة خلال مرحلة الإطلاق تجريبية حتى تُفعّل عمليات الربط الحقيقية.",
        "لا ندّعي أي شراكات حكومية أو مخزوناً مباشراً أو ضمانات أو أعداد مستخدمين. القدرات مُعلّمة بمباشر أو تجريبي أو قريباً.",
        "مُشغّل بواسطة Buy-Tech.ai. للتواصل: partners@buydubai.ai",
      ],
    },
  },
};

export default translations;
